// Helpers de fechas para la vista de horarios.
// Usamos UTC para parsing/aritmética y delegamos formato visual a Intl.DateTimeFormat.

// Parse 'YYYY-MM-DD' → Date UTC midnight.
export function parseFecha(s) {
  return new Date(s + 'T00:00:00Z');
}
export function aISO(d) {
  return d.toISOString().slice(0, 10);
}

// Lunes de la semana de `d` (ISO: lunes = inicio de semana).
export function lunesDe(d) {
  const day = d.getUTCDay(); // 0=Dom..6=Sab
  const diff = day === 0 ? -6 : 1 - day;
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + diff);
  r.setUTCHours(0, 0, 0, 0);
  return r;
}
export function domingoDe(d) {
  const lun = lunesDe(d);
  const r = new Date(lun);
  r.setUTCDate(r.getUTCDate() + 6);
  return r;
}
export function semanaActualISO() {
  const hoy = new Date();
  hoy.setUTCHours(0, 0, 0, 0);
  return {
    desde: aISO(lunesDe(hoy)),
    hasta: aISO(domingoDe(hoy)),
  };
}
export function semanaOffset({ desde }, semanas) {
  const d = parseFecha(desde);
  d.setUTCDate(d.getUTCDate() + semanas * 7);
  return {
    desde: aISO(lunesDe(d)),
    hasta: aISO(domingoDe(d)),
  };
}

// Listado de fechas (string YYYY-MM-DD) entre desde y hasta inclusive.
export function fechasEntre(desde, hasta) {
  const out = [];
  const start = parseFecha(desde);
  const end = parseFecha(hasta);
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(aISO(d));
  }
  return out;
}

function localePara(lang) {
  // i18n.resolvedLanguage da 'es', 'en', etc. Intl espera locale tipo 'es-ES'.
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

// "lunes 4 may" — primera letra mayúscula, sin año.
export function formateaDiaCorto(fechaISO, lang) {
  const d = parseFecha(fechaISO);
  const f = new Intl.DateTimeFormat(localePara(lang), {
    weekday: 'long', day: 'numeric', month: 'short', timeZone: 'UTC',
  }).format(d);
  return capPrimera(f);
}

// "21 abr" o "21 — 27 abr 2026" para el rango de semana.
export function formateaRangoSemana(desdeISO, hastaISO, lang) {
  const a = parseFecha(desdeISO);
  const b = parseFecha(hastaISO);
  const locale = localePara(lang);
  const mismoMes = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
  if (mismoMes) {
    const dia = (d) => new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }).format(d);
    const mesAnio = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(b);
    return `${dia(a)} – ${dia(b)} ${mesAnio}`;
  }
  const corto = (d) => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d);
  const anio = b.getUTCFullYear();
  return `${corto(a)} – ${corto(b)} ${anio}`;
}

function capPrimera(s) {
  if (!s) return s;
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}

// "Lunes 4 mayo 2026, 18:30"
export function formateaFechaHora(fechaISO, hora, lang) {
  const d = parseFecha(fechaISO);
  const f = new Intl.DateTimeFormat(localePara(lang), {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(d);
  return `${capPrimera(f)}, ${hora}`;
}

// XXXX-XXXX legible para mostrar el código de reserva.
export function formateaCodigoReserva(codigo) {
  if (!codigo || codigo.length !== 8) return codigo || '';
  return codigo.slice(0, 4) + '-' + codigo.slice(4);
}

// Momento actual en Europe/Madrid como { fecha: 'YYYY-MM-DD', hora: 'HH:MM' }.
// Usamos Intl para que el cálculo sea correcto en navegadores con otra TZ
// (p. ej. VPN, móviles configurados en UTC, etc).
export function ahoraEnMadrid() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  });
  const parts = {};
  for (const p of fmt.formatToParts(new Date())) parts[p.type] = p.value;
  return {
    fecha: `${parts.year}-${parts.month}-${parts.day}`,
    hora: `${parts.hour}:${parts.minute}`,
  };
}

// ¿La franja (fecha ISO + hora HH:MM) ya empezó en hora de Madrid?
// fechaISO y horaHHMM son strings; su comparación lexicográfica funciona
// porque están en formato ordenable.
export function yaEmpezada(fechaISO, horaHHMM) {
  if (!fechaISO || !horaHHMM) return false;
  const ahora = ahoraEnMadrid();
  if (fechaISO < ahora.fecha) return true;
  if (fechaISO > ahora.fecha) return false;
  return horaHHMM.slice(0, 5) <= ahora.hora;
}
